/**
 * Pings API adapter layer.
 * Wraps the BFF-based device.api.ts functions and maps to the Ping type used by UI.
 */
import type { CursorParams, CursorPaginatedResponse } from '../types/api.types'
import type { Ping } from '../types/ping.types'
import type { Geolocation } from '../types/device.types'
import type { Ping as BffPing } from './device.types'
import { listPings, getDevicePings } from './device.api'

function mapBffPing(bff: BffPing): Ping {
  const pingStatus = bff.pingStatus as Record<string, unknown> | undefined
  const pingInfo = bff.pingInfo as Record<string, unknown> | undefined

  const ipAddress = (pingStatus?.ipAddress as string)
    || (pingInfo?.PublicIpAddress as string)
    || (pingInfo?.ipAddress as string)
    || ''

  const status = (pingStatus?.status as string) || ''

  let geolocation: Geolocation | null = null
  if (pingStatus?.geolocation) {
    const geo = pingStatus.geolocation as Record<string, unknown>
    if (typeof geo.lat === 'number' && typeof geo.lon === 'number') {
      geolocation = { lat: geo.lat, lon: geo.lon }
    }
  }

  return {
    id: bff.id,
    deviceId: bff.deviceId,
    pingAt: bff.pingAt,
    ipAddress,
    status,
    geolocation,
    createdAt: bff.createdAt,
  }
}

export async function list(params?: { page?: number; pageSize?: number }) {
  const resp = await listPings({
    limit: params?.pageSize ?? 50,
  })
  return {
    data: resp.data.map(mapBffPing),
    total: 0,
    limit: resp.pagination.limit,
    offset: 0,
  }
}

export async function listCursor(params?: CursorParams): Promise<CursorPaginatedResponse<Ping>> {
  const resp = await listPings({
    limit: params?.pageSize ?? 50,
    cursor: params?.cursor,
    search: params?.search,
  })
  return {
    items: resp.data.map(mapBffPing),
    totalCount: 0,
    limit: resp.pagination.limit,
    nextCursor: resp.pagination.nextCursor,
    prevCursor: resp.pagination.prevCursor,
    hasNext: resp.pagination.hasMore,
    hasPrev: !!resp.pagination.prevCursor,
  }
}

export async function getByDevice(deviceId: string, _params?: { page?: number; pageSize?: number }) {
  const resp = await getDevicePings(deviceId, { limit: 50 })
  return {
    data: resp.data.map(mapBffPing),
    total: 0,
    limit: resp.pagination.limit,
    offset: 0,
  }
}

export async function getByDeviceCursor(deviceId: string, params?: CursorParams): Promise<CursorPaginatedResponse<Ping>> {
  const resp = await getDevicePings(deviceId, {
    limit: params?.pageSize ?? 50,
    cursor: params?.cursor,
  })
  return {
    items: resp.data.map(mapBffPing),
    totalCount: 0,
    limit: resp.pagination.limit,
    nextCursor: resp.pagination.nextCursor,
    prevCursor: resp.pagination.prevCursor,
    hasNext: resp.pagination.hasMore,
    hasPrev: !!resp.pagination.prevCursor,
  }
}
